/**
 * Typed wrapper around the training worker.
 *
 * The UI talks to the engine exclusively through this class, so replacing the
 * worker with an in-thread implementation later would be a one-file change.
 */
import type { WorkerRequest, WorkerResponse } from './protocol.ts';

type Handler<T extends WorkerResponse['type']> = (
  message: Extract<WorkerResponse, { type: T }>,
) => void;

export class TrainingClient {
  #worker: Worker;
  #handlers = new Map<WorkerResponse['type'], Set<(message: WorkerResponse) => void>>();
  #errorHandlers = new Set<(message: string) => void>();

  constructor(url: URL | string) {
    this.#worker = new Worker(url, { type: 'module', name: 'growing-mnist-training' });
    this.#worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const message = event.data;
      if (message.type === 'error') {
        this.#errorHandlers.forEach((handler) => handler(message.message));
      }
      this.#handlers.get(message.type)?.forEach((handler) => handler(message));
    };
    this.#worker.onerror = (event) => {
      const message = event.message || 'Worker crashed';
      this.#errorHandlers.forEach((handler) => handler(message));
    };
  }

  on<T extends WorkerResponse['type']>(type: T, handler: Handler<T>): () => void {
    const set = this.#handlers.get(type) ?? new Set<(message: WorkerResponse) => void>();
    set.add(handler as (message: WorkerResponse) => void);
    this.#handlers.set(type, set);
    return () => set.delete(handler as (message: WorkerResponse) => void);
  }

  onError(handler: (message: string) => void): () => void {
    this.#errorHandlers.add(handler);
    return () => this.#errorHandlers.delete(handler);
  }

  send(message: WorkerRequest, transfer: Transferable[] = []): void {
    this.#worker.postMessage(message, transfer);
  }

  terminate(): void {
    this.#worker.terminate();
    this.#handlers.clear();
    this.#errorHandlers.clear();
  }
}
